import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Printer, DownloadSimple, ArrowRight, ArrowLeft, BookOpenText } from "@phosphor-icons/react";
import api from "@/lib/api";

const FOREWORD_WORD_LIMIT = 800;

/**
 * PrintPrepareModal — two-step flow before a cookbook goes to print.
 *
 *   Step 1 — Preview:
 *     • Foreword editor (owner's introduction, up to 800 words). Persists to
 *       the cookbook via PATCH /api/cookbooks/{id} on "Save & continue".
 *     • Recipe list with each recipe's thumbnail image + title, so the owner
 *       can confirm what's going into the book BEFORE building the PDF.
 *
 *   Step 2 — Choose destination:
 *     • "Download print-ready PDF" — pulls the A4 ring-binder PDF.
 *     • "Book-format PDF for professional printing" — downloads the 6×9"
 *       book PDF for any print service or local print shop.
 */
export default function PrintPrepareModal({
  open,
  onClose,
  cookbook,
  recipes,
  recipesNeedingImages,
}) {
  const [step, setStep] = useState(1);
  const [foreword, setForeword] = useState(cookbook?.foreword || "");
  const [savingForeword, setSavingForeword] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setForeword(cookbook?.foreword || "");
    }
  }, [open, cookbook?.foreword]);

  if (!open) return null;

  const wordCount = foreword.trim() ? foreword.trim().split(/\s+/).length : 0;
  const overLimit = wordCount > FOREWORD_WORD_LIMIT;

  const saveForewordAndAdvance = async () => {
    if (overLimit) {
      toast.error(`Foreword is over the ${FOREWORD_WORD_LIMIT}-word limit — trim it down.`);
      return;
    }
    setSavingForeword(true);
    try {
      await api.patch(`/cookbooks/${cookbook.cookbook_id}`, { foreword: foreword.trim() });
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save foreword");
    } finally {
      setSavingForeword(false);
    }
  };

  const downloadA4 = async () => {
    setDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/print/cookbook/${cookbook.cookbook_id}/a4`;
      const token = localStorage.getItem("rf_token");
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `PDF request failed (HTTP ${res.status}).`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${(cookbook.title || "cookbook").replace(/\s+/g, "_")}_A4_ring_binder.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error(e?.message || "PDF download failed", { duration: 10_000 });
    } finally { setDownloading(false); }
  };

  const downloadBook = async () => {
    if (recipesNeedingImages?.length > 0) {
      toast.error(`${recipesNeedingImages.length} recipe${recipesNeedingImages.length === 1 ? "" : "s"} still need an image before you download the book-format PDF.`);
      return;
    }
    setDownloading(true);
    try {
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/print/cookbook/${cookbook.cookbook_id}/lulu`;
      const token = localStorage.getItem("rf_token");
      const res = await fetch(pdfUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${(cookbook.title || "cookbook").replace(/\s+/g, "_")}_6x9_book.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Book-format PDF downloaded — send it to any print service or print shop.", { duration: 6000 });
    } catch (e) {
      toast.error(e?.message || "Book PDF generation failed");
    } finally { setDownloading(false); }
  };

  const thumb = (r) => {
    if (r.ai_image_active && r.ai_image_url) return r.ai_image_url;
    const first = (r.images || []).find((u) => u);
    return first || null;
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="print-prepare-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-rf-ink/10">
          <div className="flex items-center gap-3">
            <BookOpenText size={22} weight="duotone" className="text-rf-orange" />
            <div>
              <h2 className="font-display font-black text-rf-ink text-lg">Prepare for print</h2>
              <p className="text-xs text-rf-ink-soft">Step {step} of 2 — {step === 1 ? "preview your cookbook" : "choose how to print"}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-rf-ink-soft hover:text-rf-ink p-1" data-testid="print-prepare-close">
            <X size={20} />
          </button>
        </div>

        {step === 1 && (
          <div className="px-6 py-5 space-y-6">
            {/* Foreword editor */}
            <section>
              <label className="block text-[11px] font-black uppercase tracking-wider text-rf-orange mb-2">
                Foreword (optional)
              </label>
              <textarea
                rows={5}
                value={foreword}
                onChange={(e) => setForeword(e.target.value)}
                maxLength={5500}
                placeholder="Write a short introduction for your family cookbook — who it's for, where the recipes came from, a dedication. Up to 800 words."
                data-testid="print-prepare-foreword"
                className="w-full rounded-lg border border-rf-ink/15 px-3 py-2 text-rf-ink focus:outline-none focus:border-rf-amber leading-relaxed"
              />
              <p className={`text-[11px] font-bold mt-1 ${overLimit ? "text-red-600" : "text-rf-ink-soft"}`} data-testid="print-prepare-wordcount">
                {wordCount} / {FOREWORD_WORD_LIMIT} words{overLimit && " — please trim"}
              </p>
            </section>

            {/* Recipe preview grid */}
            <section>
              <label className="block text-[11px] font-black uppercase tracking-wider text-rf-orange mb-2">
                Recipes in this book ({recipes.length})
              </label>
              <div className="rounded-lg border border-rf-ink/10 max-h-80 overflow-y-auto divide-y divide-rf-ink/5" data-testid="print-prepare-recipe-list">
                {recipes.length === 0 ? (
                  <p className="p-4 text-sm text-rf-ink-soft text-center">No recipes yet — add at least one before printing.</p>
                ) : recipes.map((r, idx) => {
                  const t = thumb(r);
                  return (
                    <div key={r.recipe_id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`print-prepare-row-${r.recipe_id}`}>
                      <span className="text-xs font-bold text-rf-ink-soft w-6 text-right">{idx + 1}.</span>
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-rf-cream-alt flex-shrink-0">
                        {t ? (
                          <img src={t} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-rf-ink-soft/40 text-xs">no img</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-rf-ink truncate">{r.title || "Untitled"}</p>
                        {r.serves && <p className="text-[11px] text-rf-ink-soft">Serves {r.serves}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-rf-ink-soft mt-2">
                Your printed book will include a cover, your foreword, a Recipe Index with page numbers, then every recipe above — each with its photo.
              </p>
            </section>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-outline-ink !py-2 !px-4 text-sm"
                data-testid="print-prepare-cancel"
              >Cancel</button>
              <button
                type="button"
                onClick={saveForewordAndAdvance}
                disabled={savingForeword || overLimit || recipes.length === 0}
                data-testid="print-prepare-next"
                className="btn-amber !py-2 !px-5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                {savingForeword ? "Saving…" : "Next"} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-rf-ink-soft">
              Pick how you want to print this cookbook. You can always come back later and switch.
            </p>

            <button
              type="button"
              onClick={downloadA4}
              disabled={downloading}
              data-testid="print-prepare-download-pdf"
              className="w-full text-left border-2 border-rf-ink/10 rounded-2xl p-5 hover:border-rf-orange hover:bg-rf-amber/5 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-4">
                <DownloadSimple size={28} weight="duotone" className="text-rf-orange flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-display font-black text-rf-ink text-base mb-1">Download print-ready PDF</h3>
                  <p className="text-sm text-rf-ink-soft">A4 ring-binder format — one recipe per page, free. Print at home or any local printer.</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={downloadBook}
              disabled={downloading}
              data-testid="print-prepare-book-pdf"
              className="w-full text-left border-2 border-rf-ink/10 rounded-2xl p-5 hover:border-rf-orange hover:bg-rf-amber/5 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-4">
                <Printer size={28} weight="duotone" className="text-rf-orange flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-display font-black text-rf-ink text-base mb-1">Book-format PDF for professional printing</h3>
                  <p className="text-sm text-rf-ink-soft">
                    Downloads a 6×9&quot; book-layout PDF, ready for any print-on-demand service or local print shop to turn into a hardback or paperback keepsake.
                  </p>
                </div>
              </div>
            </button>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-sm text-rf-ink-soft hover:text-rf-ink inline-flex items-center gap-1"
                data-testid="print-prepare-back"
              >
                <ArrowLeft size={14} /> Back to preview
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn-outline-ink !py-2 !px-4 text-sm"
              >Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
